import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, SupportModal } from '../common';
import { messagesService } from '../../services';
import logo from '../../assets/logo.svg';
import styles from '../../styles/components/Header.module.css';

function Header({ user }) {
  const { t } = useTranslation();
  const [avatarError, setAvatarError] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await messagesService.getUnreadCount();
        if (response.success) {
          setUnreadCount(response.data.count || 0);
        }
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    fetchUnreadCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Seitentitel basierend auf Route ermitteln
  const getPageTitle = () => {
    // Exakte Übereinstimmung prüfen
    const titleMap = {
      '/dashboard': t('header.pageTitles.store'),
      '/earnings': t('header.pageTitles.earnings'),
      '/promotion': t('header.pageTitles.promotion'),
      '/settings': t('header.pageTitles.settings'),
      '/messages': t('header.pageTitles.messages'),
      '/products/new': t('header.pageTitles.newProduct'),
      '/dashboard/purchases': t('header.pageTitles.myPurchases'),
    };
    if (titleMap[location.pathname]) {
      return titleMap[location.pathname];
    }
    // Produkt bearbeiten Route
    if (location.pathname.match(/^\/products\/\d+\/edit$/)) {
      return t('header.pageTitles.editProduct');
    }
    // Fallback
    return t('header.pageTitles.default');
  };

  // Get initials from user name (max 2 characters)
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Reset avatar error when user avatar changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  const pageTitle = getPageTitle();

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          {/* Logo + Seitentitel */}
          <div className={styles.titleSection}>
            <Link to="/dashboard" className={styles.logoIcon}>
              <img src={logo} alt="MoneMee" width="28" height="28" />
            </Link>
            <h1 className={styles.pageTitle}>{pageTitle}</h1>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            {/* Support */}
            <button
              className={styles.iconButton}
              title={t('header.helpAndSupport')}
              onClick={() => setShowSupportModal(true)}
            >
              <Icon name="helpCircle" size="md" />
            </button>

            {/* Messages */}
            <Link to="/messages" className={styles.iconButton} title={t('header.messages')}>
              <Icon name="message" size="md" />
              {unreadCount > 0 && (
                <span className={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </Link>

            {/* User Avatar - Links to Account Settings */}
            <Link to="/settings?tab=account" className={styles.avatar} aria-label={t('header.accountSettings')}>
              {user?.avatar && !avatarError ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className={styles.avatarImage}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span className={styles.avatarInitials}>
                  {getInitials(user?.name)}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </>
  );
}

export default Header;
